/** @format */

import { DAY_MIN, PAD, SNAP, getDayS } from './constants';
import { earlyDragToneColor } from './earlyDragOverrun';
import {
  minTripGroupHours,
  normalizeGroup,
  readGroupField,
  syncTechnicianIds,
} from './groupSchema';
import { m2t, t2m } from './time';

const OVERLAY_FONT = "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif";

export function findDayCellAt(clientX, clientY) {
  const stack = document.elementsFromPoint(clientX, clientY);
  for (let i = 0; i < stack.length; i += 1) {
    const cell = stack[i].closest?.('.dc[data-technician-id]');
    if (cell) return cell;
  }
  return null;
}

export function cellYToMin(
  rect,
  clientY,
  originMin = getDayS(),
  usable = undefined,
) {
  const span =
    Number.isFinite(usable) && usable > 0 ? usable : rect.height - PAD * 2;
  const relY = Math.max(0, Math.min(clientY - rect.top - PAD, span));
  return originMin + Math.round((relY / span) * DAY_MIN);
}

export function snapDrawerMin(rawMin, durationMin, minStart) {
  const snapped = Math.round(rawMin / SNAP) * SNAP;
  const floor = Number.isFinite(minStart) ? minStart : getDayS();
  const dayEnd = floor + DAY_MIN;
  return Math.max(floor, Math.min(snapped, dayEnd - durationMin));
}

export function minToCellY(
  rect,
  minutes,
  originMin = getDayS(),
  usable = undefined,
) {
  const span =
    Number.isFinite(usable) && usable > 0 ? usable : rect.height - PAD * 2;
  return rect.top + PAD + ((minutes - originMin) / DAY_MIN) * span;
}

function createOverlayLabel() {
  const label = document.createElement('div');
  label.style.cssText = `position:fixed;font-size:10px;font-weight:700;color:#00B37E;background:#fff;padding:2px 6px;border-radius:4px;pointer-events:none;z-index:9999;border:0.5px solid #00B37E;display:none;white-space:nowrap;font-family:${OVERLAY_FONT}`;
  return label;
}

export function createDispoDragOverlay() {
  const lineBase =
    'position:fixed;height:2px;background:#00B37E;pointer-events:none;z-index:9998;border-radius:1px;display:none';
  const snapStartLine = document.createElement('div');
  snapStartLine.style.cssText = lineBase;
  const snapEndLine = document.createElement('div');
  snapEndLine.style.cssText = `${lineBase};box-shadow:0 0 0 1px rgba(255,255,255,0.6)`;
  const snapLbl = createOverlayLabel();
  const centerLbl = createOverlayLabel();
  document.body.appendChild(snapStartLine);
  document.body.appendChild(snapEndLine);
  document.body.appendChild(snapLbl);
  document.body.appendChild(centerLbl);

  return {
    snapStartLine,
    snapEndLine,
    snapLbl,
    centerLbl,
    remove() {
      snapStartLine.remove();
      snapEndLine.remove();
      snapLbl.remove();
      centerLbl.remove();
    },
    hide() {
      snapStartLine.style.display = 'none';
      snapEndLine.style.display = 'none';
      snapLbl.style.display = 'none';
      centerLbl.style.display = 'none';
    },
  };
}

export function positionDispoDragHighlight(
  cell,
  startMin,
  durationMin,
  overlay,
  { centerLabel, centerEmphasis, snapText, snapTone, originMin } = {},
) {
  const { snapStartLine, snapEndLine, snapLbl, centerLbl } = overlay;
  if (!cell) {
    overlay.hide();
    return;
  }
  const rect = cell.getBoundingClientRect();
  const usable = Number(cell.dataset.inner) || rect.height - PAD * 2;
  const origin = Number.isFinite(originMin) ? originMin : getDayS();
  const endMin = startMin + durationMin;
  const startY = minToCellY(rect, startMin, origin, usable);
  const endY = minToCellY(rect, endMin, origin, usable);
  const left = `${rect.left}px`;
  const width = `${rect.width}px`;
  const tone = earlyDragToneColor(snapTone);

  snapStartLine.style.left = left;
  snapStartLine.style.width = width;
  snapStartLine.style.top = `${startY}px`;
  snapStartLine.style.display = 'block';
  snapStartLine.style.background = tone;

  snapEndLine.style.left = left;
  snapEndLine.style.width = width;
  snapEndLine.style.top = `${endY}px`;
  snapEndLine.style.display = 'block';

  snapLbl.textContent = snapText || `${m2t(startMin)} → ${m2t(endMin)}`;
  snapLbl.style.color = tone;
  snapLbl.style.borderColor = tone;
  snapLbl.style.left = `${rect.left + 4}px`;
  snapLbl.style.top = `${Math.min(startY, endY) - 10}px`;
  snapLbl.style.transform = '';
  snapLbl.style.display = 'block';

  if (!centerLbl) return;
  if (centerLabel) {
    centerLbl.replaceChildren();
    if (centerEmphasis) {
      const prefix = document.createElement('span');
      prefix.textContent = centerLabel;
      const hour = document.createElement('span');
      hour.textContent = centerEmphasis;
      hour.style.fontSize = '14px';
      hour.style.lineHeight = '1';
      centerLbl.append(prefix, hour);
    } else {
      centerLbl.textContent = centerLabel;
    }
    centerLbl.style.left = `${rect.left + rect.width / 2}px`;
    centerLbl.style.top = `${(startY + endY) / 2}px`;
    centerLbl.style.transform = 'translate(-50%, -50%)';
    centerLbl.style.display = 'block';
  } else {
    centerLbl.style.display = 'none';
  }
}

function ensureGroupShape(group, durationH, startStr, endStr) {
  const technicianCount =
    Number(readGroupField(group, 'technician_count')) || 1;
  return {
    id: readGroupField(group, 'id') || '',
    technician_count: technicianCount,
    duration: Number(readGroupField(group, 'duration')) || durationH,
    start_time: readGroupField(group, 'start_time') || startStr,
    end_time: readGroupField(group, 'end_time') || endStr,
    technician_ids: syncTechnicianIds(
      readGroupField(group, 'technician_ids'),
      technicianCount,
    ),
  };
}

/** Remove a technician from every group before assigning them to a single slot. */
export function clearTechnicianFromGroups(groups, technicianId) {
  if (technicianId == null) return groups;
  return groups.map((group) => {
    const technicianCount =
      Number(readGroupField(group, 'technician_count')) || 1;
    const techIds = syncTechnicianIds(
      readGroupField(group, 'technician_ids'),
      technicianCount,
    );
    return {
      ...group,
      technician_ids: techIds.map((id) =>
        id != null && Number(id) === Number(technicianId) ? null : id,
      ),
    };
  });
}

/** Assign technician + slot times on the first group with a free slot. */
export function buildGroupsAfterDrop(
  intervention,
  technicianId,
  startStr,
  endStr,
  { replaceTechnicianId = null } = {},
) {
  const durationH = (t2m(endStr) - t2m(startStr)) / 60;
  const baseGroups = intervention.groups?.length
    ? intervention.groups.map((g) =>
        ensureGroupShape(g, durationH, startStr, endStr),
      )
    : [
        {
          id: '',
          technician_count: 1,
          duration: durationH,
          start_time: startStr,
          end_time: endStr,
          technician_ids: [null],
        },
      ];

  let assigned = false;
  let groups = baseGroups.map((group) => {
    const technicianCount = group.technician_count || 1;
    return {
      ...group,
      technician_ids: syncTechnicianIds(group.technician_ids, technicianCount),
    };
  });

  let replacedOriginal = false;
  if (
    replaceTechnicianId != null &&
    Number(replaceTechnicianId) !== Number(technicianId)
  ) {
    groups = groups.map((group) => {
      let groupReplaced = false;
      const technicianIds = (group.technician_ids || []).map((id) => {
        if (id == null || Number(id) !== Number(replaceTechnicianId)) return id;
        groupReplaced = true;
        replacedOriginal = true;
        return technicianId;
      });
      if (!groupReplaced) return group;
      return {
        ...group,
        technician_ids: technicianIds,
        start_time: startStr,
        end_time: endStr,
        duration: durationH,
      };
    });
  }
  if (replacedOriginal) return groups;
  groups = clearTechnicianFromGroups(groups, technicianId);

  groups = groups.map((group) => {
    if (assigned) return group;
    const slotIndex = group.technician_ids.findIndex((id) => id == null);
    if (slotIndex < 0) return group;
    assigned = true;
    const technicianIds = [...group.technician_ids];
    technicianIds[slotIndex] = technicianId;
    return {
      ...group,
      technician_ids: technicianIds,
      start_time: startStr,
      end_time: endStr,
      duration: durationH,
    };
  });

  if (!assigned && groups[0]) {
    const technicianIds = [...groups[0].technician_ids];
    technicianIds[0] = technicianId;
    groups = [
      {
        ...groups[0],
        technician_ids: technicianIds,
        start_time: startStr,
        end_time: endStr,
        duration: durationH,
      },
      ...groups.slice(1),
    ];
  }

  return groups;
}

/** Assign technician + slot times using the work-item crew size and forced techs. */
export function buildGroupsForWorkItem(
  workItem,
  technicianId,
  startStr,
  endStr,
) {
  if (workItem?.kind === 'trip' && (workItem.trip || workItem.groups)) {
    return buildGroupsAfterDrop(
      workItem.trip || workItem,
      technicianId,
      startStr,
      endStr,
    );
  }
  const rawH = (t2m(endStr) - t2m(startStr)) / 60;
  const durationH = minTripGroupHours(rawH);
  const end =
    Number.isFinite(rawH) && durationH > rawH
      ? m2t(t2m(startStr) + Math.round(durationH * 60))
      : endStr;
  const crew = Math.max(Number(workItem?.minimumTechnicianCount) || 1, 1);
  const forced = (workItem?.forcedTechnicianIds || [])
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id));
  const assignedTechnicianId = Number(technicianId);
  const technicianIds = Array.from({ length: crew }, () => null);
  technicianIds[0] = Number.isFinite(assignedTechnicianId)
    ? assignedTechnicianId
    : technicianId;
  let slot = 1;
  forced.forEach((id) => {
    if (slot >= crew) return;
    if (Number(id) === Number(technicianId)) return;
    technicianIds[slot] = id;
    slot += 1;
  });
  return [
    {
      id: '',
      technician_count: crew,
      duration: durationH,
      start_time: startStr,
      end_time: end,
      technician_ids: technicianIds,
    },
  ];
}

/** Move an existing planned slot to another technician/day/time. */
export function buildGroupsAfterPlanMove(
  intervention,
  fromTechnicianId,
  toTechnicianId,
  startStr,
  endStr,
) {
  const durationH = (t2m(endStr) - t2m(startStr)) / 60;
  let groups = (intervention.groups || []).map((g) =>
    ensureGroupShape(g, durationH, startStr, endStr),
  );

  groups = clearTechnicianFromGroups(groups, toTechnicianId);

  let moved = false;
  groups = groups.map((group) => {
    if (moved) return group;
    const slotIndex = group.technician_ids.findIndex(
      (id) => id != null && Number(id) === Number(fromTechnicianId),
    );
    if (slotIndex < 0) return group;
    moved = true;
    const technicianIds = [...group.technician_ids];
    technicianIds[slotIndex] = toTechnicianId;
    return {
      ...group,
      technician_ids: technicianIds,
      start_time: startStr,
      end_time: endStr,
      duration: durationH,
    };
  });

  if (!moved) {
    return buildGroupsAfterDrop(intervention, toTechnicianId, startStr, endStr);
  }
  return groups;
}

/** Update start/end/duration for groups that contain this technician (grid resize). */
export function buildGroupsAfterTimeResize(
  intervention,
  technicianId,
  startStr,
  endStr,
) {
  const durationH = (t2m(endStr) - t2m(startStr)) / 60;
  let updated = false;
  const groups = (intervention.groups || []).map((g) => {
    const shaped = ensureGroupShape(g, durationH, startStr, endStr);
    const techIds = shaped.technician_ids || [];
    const hasTech = techIds.some(
      (id) => id != null && Number(id) === Number(technicianId),
    );
    if (!hasTech) return shaped;
    updated = true;
    return {
      ...shaped,
      start_time: startStr,
      end_time: endStr,
      duration: durationH,
    };
  });
  if (!updated) {
    return buildGroupsAfterPlanMove(
      intervention,
      technicianId,
      technicianId,
      startStr,
      endStr,
    );
  }
  return groups;
}

/** Clear all technician assignments (removed from planning view). */
export function buildGroupsClearPlanning(intervention) {
  return (intervention.groups || []).map((g, index) => {
    const normalized = normalizeGroup(g, 0, index);
    return {
      ...normalized,
      technician_ids: Array.from(
        { length: normalized.technician_count },
        () => null,
      ),
    };
  });
}

/** Unassign one technician after removing their block from the grid. */
export function buildGroupsAfterRemovingTechnician(intervention, technicianId) {
  const groups = clearTechnicianFromGroups(
    (intervention.groups || []).map((g, index) => normalizeGroup(g, 0, index)),
    technicianId,
  );
  const hasAssignedTechnician = groups.some((group) =>
    (group.technician_ids || []).some((id) => id != null),
  );
  // No technicians left → clear the date so the trip leaves the day indicator.
  return {
    groups,
    date: hasAssignedTechnician ? intervention.date || '' : '',
  };
}
